import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Timer } from './timer';
export { Timer };
import { timerSchema } from '@schemas/business-objects/timer';
import { TenantFormGroup } from './tenant-form-group';
import { TokenFormGroup } from './token-form-group';
import type { ConditionWatcher } from './condition-watcher';
import type { MessageListener } from './message-listener';
import type { PendingStateUpdate } from './pending-state-update';

export class TimerFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    boundaryEventId: SchemaFormControl;
    interrupting: SchemaFormControl;
    fireAt: SchemaFormControl;
    repeating: SchemaFormControl;
    repeatLimit: SchemaFormControl;
    fireCount: SchemaFormControl;
    status: SchemaFormControl;
    createdAt: SchemaFormControl;
    updatedAt: SchemaFormControl;
    tokenId: SchemaFormControl;
    token: TokenFormGroup;
    tenantId: SchemaFormControl;
    tenant: TenantFormGroup;
  };

  declare value: Partial<Timer>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(timerSchema, Timer, data, options);
  }

  get object(): Timer {
    return new Timer(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'token':
        return new TokenFormGroup();
      case 'tenant':
        return new TenantFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Timer[]> {
    return Timer.find(filter);
  }

  static async findOne(filter?: any): Promise<Timer | null> {
    return Timer.findOne(filter);
  }

  static async findById(id: string, filter?: any): Promise<Timer> {
    return Timer.findById(id, filter);
  }

  static async create(data: Partial<Timer>): Promise<Timer> {
    return Timer.create(data as any);
  }

  static async updateById(id: string, updates: Partial<Timer>): Promise<Timer> {
    return Timer.updateById(id, updates as any);
  }

  static async deleteById(id: string): Promise<boolean> {
    return Timer.deleteById(id);
  }

  async fire(): Promise<any> {
    const instance = new Timer(this.getRawValue());
    return instance.fire();
  }
}

export class TimerFormArray extends PersistedFormArray {
  declare controls: TimerFormGroup[];

  readonly entityName = 'Timer' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(timerSchema, Timer, options);
  }

  protected override createItemGroup() {
    return new TimerFormGroup();
  }

  get array(): Timer[] {
    return this.controls.map((group: TimerFormGroup) => group.object);
  }
}

export class TimerPersistedArray extends PersistedArray<Timer> {
  readonly entityName = 'Timer' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Timer, options);
  }
}
